import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import axiosClient from '../services/api';

import boleumlogo from "../assets/boleumlogo.png";

import drillingImg from '../assets/images/obs-l.jpg';
import productionImg from '../assets/images/obs-d.jpg';
import reservoirImg from '../assets/images/obs-k.jpg';
import facilitiesImg from '../assets/images/obs-e.jpg';
import hseImg from '../assets/images/obs-c.jpg';
import geoscienceImg from '../assets/images/obs-f.jpg';
import automationImg from '../assets/images/obs-g.jpg';
import maintenanceImg from '../assets/images/obs-i.jpg';
import managementImg from '../assets/images/obs-h.jpg';
import digitalImg from '../assets/images/obs-j.jpg';

import careerImg from '../assets/images/obs-sa.jpg';
import skillImg from '../assets/images/obs-sf.jpg';
import growthImg from '../assets/images/obs-sd.jpg';
import certImg from '../assets/images/obs-sb.jpg';
import eduImg from '../assets/images/obs-sc.jpg';
import entImg from '../assets/images/obs-se.jpg';
import { useToast } from '../components/ToastContainer';

const StudentOnboardingPage = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { user, fetchProfile } = useAuthStore();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Learning preferences options
    const preferenceOptions = [
        { id: 'Drilling Engineering', title: 'Drilling Engineering', image: drillingImg },
        { id: 'Production Engineering', title: 'Production Engineering', image: productionImg },
        { id: 'Reservoir Engineering', title: 'Reservoir Engineering', image: reservoirImg },
        { id: 'Process & Facilities Engineering', title: 'Process & Facilities Engineering', image: facilitiesImg },
        { id: 'Health, Safety & Environment (HSE)', title: 'Health, Safety & Environment (HSE)', image: hseImg },
        { id: 'Subsurface & Geoscience', title: 'Subsurface & Geoscience', image: geoscienceImg },
        { id: 'Instrumentation, Control & Automation', title: 'Instrumentation, Control & Automation', image: automationImg },
        { id: 'Maintenance & Reliability Engineering', title: 'Maintenance & Reliability Engineering', image: maintenanceImg },
        { id: 'Project & Asset Management', title: 'Project & Asset Management', image: managementImg },
        { id: 'Digital Transformation in Oil & Gas', title: 'Digital Transformation in Oil & Gas', image: digitalImg }
    ];

    // Learning goals options
    const goalOptions = [
        { id: 'Career Advancement', title: 'Career Advancement', image: careerImg },
        { id: 'Skill Development', title: 'Skill Development', image: skillImg },
        { id: 'Personal Growth', title: 'Personal Growth', image: growthImg },
        { id: 'Certification', title: 'Certification', image: certImg },
        { id: 'Academic Credit', title: 'Academic Credit', image: eduImg },
        { id: 'Entrepreneurship', title: 'Entrepreneurship', image: entImg }
    ];


    // Form state for step 2
    const [formData, setFormData] = useState({
        dateOfBirth: '',
        country: 'Nigeria',
        state: '',
        city: '',
        gender: '',
        educationLevel: '',
        fieldOfStudy: '',
        bio: ''
    });

    // Selected preferences and goals
    const [selectedPreferences, setSelectedPreferences] = useState([]);
    const [selectedGoals, setSelectedGoals] = useState([]);

    // Fetch onboarding status on mount
    useEffect(() => {
        const fetchOnboardingStatus = async () => {
            try {
                const response = await axiosClient.get('/users/onboarding');
                if (response.data.success) {
                    setCurrentStep(response.data.data.step);
                    if (user?.learningPreferences) {
                        setSelectedPreferences(user.learningPreferences);
                    }
                    if (user?.learningGoals) {
                        setSelectedGoals(user.learningGoals);
                    }
                    if (user?.dateOfBirth) {
                        setFormData(prev => ({
                            ...prev,
                            dateOfBirth: user.dateOfBirth.split('T')[0],
                            country: user.country || '',
                            state: user.state || '',
                            city: user.city || '',
                            gender: user.gender || '',
                            educationLevel: user.educationLevel || '',
                            fieldOfStudy: user.fieldOfStudy || '',
                            bio: user.bio || ''
                        }));
                    }
                }
            } catch (error) {
                console.error('Error fetching onboarding status:', error);
                setError('Failed to load onboarding data');
            }
        };

        if (user) {
            fetchOnboardingStatus();
        }
    }, [user]);

    // Handle preference selection
    const handlePreferenceSelect = (preferenceId) => {
        setSelectedPreferences(prev =>
            prev.includes(preferenceId)
                ? prev.filter(id => id !== preferenceId)
                : [...prev, preferenceId]
        );
    };

    // Handle goal selection
    const handleGoalSelect = (goalId) => {
        setSelectedGoals(prev =>
            prev.includes(goalId)
                ? prev.filter(id => id !== goalId)
                : [...prev, goalId]
        );
    };

    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "bio") {
            const words = value.trim().split(/\s+/).filter(Boolean);
            if (words.length > 2000) {
                toast.info("Bio cannot exceed 1000 words");
                return;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Submit current step
    const handleSubmitStep = async () => {
        if (isLoading) return;

        setIsLoading(true);
        setError(null);

        try {
            let data = {};

            if (currentStep === 1) {
                if (selectedPreferences.length === 0) {
                    setError('Please select at least one learning preference');
                    setIsLoading(false);
                    return;
                }
                data = { learningPreferences: selectedPreferences };
            }

            if (currentStep === 2) {
                if (!formData.dateOfBirth || !formData.country || !formData.educationLevel) {
                    setError('Please fill in all required fields');
                    setIsLoading(false);
                    return;
                }
                data = { ...formData };
            }

            if (currentStep === 3) {
                if (selectedGoals.length === 0) {
                    setError('Please select at least one learning goal');
                    setIsLoading(false);
                    return;
                }
                data = { learningGoals: selectedGoals };
            }

            const response = await axiosClient.put('/users/onboarding', {
                step: currentStep,
                data
            });

            if (response.data.success) {
                if (currentStep < 3) {
                    setCurrentStep(currentStep + 1);
                } else {
                    await fetchProfile();
                    navigate('/student/dashboard');
                }
            }
        } catch (error) {
            console.error('Error submitting onboarding step:', error);
            setError(error.response?.data?.error || 'Failed to save onboarding data');
        } finally {
            setIsLoading(false);
        }
    };

    // Go to previous step
    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="flex items-center space-x-2 mb-8">
                    <img
                        src={boleumlogo}
                        alt="BoleumLearn Logo"
                        className="h-8 w-8 object-contain"
                    />

                    {/* Brand Text */}
                    <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        BoleumLearn
                    </span>
                </div>

            </div>
            <div className="max-w-4xl mx-auto px-4">
                {/* Progress Bar */}
                <div className="mb-8">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Step {currentStep} of 3</span>
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {Math.round((currentStep / 3) * 100)}% Complete
                        </span>
                    </div>
                    <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                        <div
                            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(currentStep / 3) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Step Content */}
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8">
                    {error && (
                        <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {currentStep === 1 && (
                        <>
                            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">What are you interested in?</h2>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                Select the categories that match your learning interests. This helps us recommend the right courses for you.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                                {preferenceOptions.map((option) => (
                                    <div
                                        key={option.id}
                                        onClick={() => handlePreferenceSelect(option.id)}
                                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${selectedPreferences.includes(option.id)
                                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                                            }`}
                                    >
                                        <img
                                            src={option.image}
                                            alt={option.title}
                                            className="w-25 h-16 mx-auto mb-3 rounded-lg"
                                        />
                                        <h3 className="text-center font-medium text-neutral-900 dark:text-white">{option.title}</h3>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Tell us about yourself</h2>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                Help us personalize your learning experience by sharing some information about yourself.
                            </p>
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Date of Birth *
                                        </label>
                                        <input
                                            type="date"
                                            name="dateOfBirth"
                                            value={formData.dateOfBirth}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Country *
                                        </label>
                                        <input
                                            type="text"
                                            name="country"
                                            disabled
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter your country"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            State
                                        </label>
                                        <p className="text-xs text-primary-600 dark:text-neutral-400 mb-2">
                                            State must start with a capital letter e.g. <span className="font-medium text-neutral-700 dark:text-neutral-200">Delta State</span>
                                        </p>
                                        <input
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter your state"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            City
                                        </label>
                                        <p className="text-xs text-primary-600 dark:text-neutral-400 mb-2">
                                            City must start with a capital letter e.g. <span className="font-medium text-neutral-700 dark:text-neutral-200">Warri</span>
                                        </p>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter your city"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Gender *
                                        </label>
                                        <select
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Education Level *
                                        </label>
                                        <select
                                            name="educationLevel"
                                            value={formData.educationLevel}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        >
                                            <option value="">Select education level</option>
                                            <option value="High School">High School</option>
                                            <option value="Bachelor">Bachelor's Degree</option>
                                            <option value="Master">Master's Degree</option>
                                            <option value="PhD">PhD</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Field of Study
                                        </label>
                                        <input
                                            type="text"
                                            name="fieldOfStudy"
                                            value={formData.fieldOfStudy}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="What did you study?"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                        Bio (Optional)
                                    </label>
                                    <textarea
                                        name="bio"
                                        value={formData.bio}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        placeholder="Tell us a bit about yourself and your learning goals..."
                                    />
                                    <p className="text-xs text-neutral-500 dark:text-neutral-300 mt-1">
                                        {formData.bio.trim().split(/\s+/).filter(Boolean).length}/2000 words
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {currentStep === 3 && (
                        <>
                            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Why are you here?</h2>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                Select the goals that best describe why you want to learn with us. This helps us tailor your learning journey.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                {goalOptions.map((option) => (
                                    <div
                                        key={option.id}
                                        onClick={() => handleGoalSelect(option.id)}
                                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${selectedGoals.includes(option.id)
                                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                                            }`}
                                    >
                                        <img
                                            src={option.image}
                                            alt={option.title}
                                            className="w-25 h-16 mx-auto mb-3 rounded-lg"
                                        />
                                        <h3 className="text-center font-medium text-neutral-900 dark:text-white">{option.title}</h3>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8">
                        {currentStep > 1 && (
                            <button
                                type="button"
                                onClick={handlePreviousStep}
                                className="px-6 py-3 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                            >
                                Previous
                            </button>
                        )}
                        {currentStep === 1 && (
                            <div></div> // Empty div for alignment
                        )}
                        <button
                            type="button"
                            onClick={handleSubmitStep}
                            disabled={isLoading}
                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            {isLoading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </span>
                            ) : currentStep === 3 ? 'Complete Onboarding' : 'Next Step'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentOnboardingPage;